/**
 * 작성자 : 한영광
 * 날짜 : 2023.05.22
 * 기능 : 주소검색 버튼 클릭 시 daum 주소검색 팝업 띄워주는 컴포넌트
 */

/**
 * 수정자 : 박승호
 * 날짜 : 2025.12.02
 * 기능 : 주소검색 모달창 컴포넌트 추가
 */

import { Dispatch, SetStateAction, useEffect, useCallback } from 'react';
import { useDaumPostcodePopup, DaumPostcodeEmbed } from 'react-daum-postcode';
import { useTranslation } from 'react-i18next';
import { BtnGray } from './Button';
import { IoSearch } from 'react-icons/io5';
import { Modal, ModalBackground } from '../assets/styles/Modal';
import { IModal } from 'customTypes';
import styled from 'styled-components';
import getAddressInfo from '../utils/getAddressInfo';

const AddressModalRoot = styled.div`
  &.modal {
    height: 60vh;
  }

  .inputForm-body {
    height: calc(60vh - 54px);

    > div {
      height: 100% !important;
    }
  }

  @media screen and (max-width: 768px) {
    &.modal {
      height: 100dvh;
    }

    .inputForm-body {
      height: calc(100dvh - 54px);
    }
  }
`;

interface IAddressSearch {
  setState: Dispatch<SetStateAction<any>>;
  disabled?: boolean;
  className?: string;
  useIconOnly?: boolean;
}

interface IAddressSearchModal {
  openModal: IModal;
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  setState: Dispatch<SetStateAction<any>>;
  className?: string;
}

// 주소검색 새창
export const AddressSearch = ({ setState, disabled, className, useIconOnly = false }: IAddressSearch) => {
  const { t } = useTranslation();
  const open = useDaumPostcodePopup();
  const options = {
    width: '500px',
    height: '420px',
    top: 200,
    left: 700,
  };

  const handleComplete = async (data: any) => {
    const { address, zonecode } = data;
    // const { latitude, longitude } = await getAddressInfo(address);
    setState((prev: any) => ({ ...prev, address1: address, postNum: zonecode }));
  };

  const handleClick = () => {
    // Flutter 환경인지 확인
    const isFlutter = window.FlutterWebView !== undefined;

    if (isFlutter) {
      // Flutter의 주소 검색 호출
      window.FlutterWebView.postMessage('openAddressSearch');
    } else {
      // 웹 환경에서의 주소 검색
      open({ onComplete: handleComplete, ...options });
    }
  };

  useEffect(() => {
    // Flutter에서 호출할 함수를 전역 객체에 등록
    window.handleAddressSelected = (data: any) => {
      const { address, zonecode, latitude, longitude } = data;
      setState((prev: any) => ({
        ...prev,
        address1: address,
        postNum: zonecode,
        latitude,
        longitude,
      }));
    };

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      if (window.handleAddressSelected) {
        window.handleAddressSelected = () => {};
      }
    };
  }, [setState]);

  return (
    <BtnGray type='button' className={className} onClick={handleClick} disabled={disabled}>
      {useIconOnly ? <IoSearch size={16} /> : t('주소검색')}
    </BtnGray>
  );
};

// 주소검색 모달창
export const AddressSearchModal = ({ openModal, setOpenModal, setState, className = '' }: IAddressSearchModal) => {
  const { t } = useTranslation();

  const handleComplete = useCallback(async (data: any) => {
    const { address, zonecode } = data;
    const { latitude, longitude } = await getAddressInfo(address);
    setState((prev: any) => ({ ...prev, address1: address, postNum: zonecode, latitude, longitude }));
    onClickClose();
  }, []);

  // 모달 닫기 버튼 클릭 함수
  const onClickClose = useCallback(() => {
    setOpenModal((prev: IModal) => ({ ...prev, status: false }));
  }, []);

  return (
    <ModalBackground onClick={onClickClose} role='presentation'>
      <Modal>
        <AddressModalRoot className='modal' role='presentation' onClick={(event) => event.stopPropagation()}>
          <div className='inputForm-head'>
            <h5>{t('주소검색')}</h5>
            <div className='closeBtn' onClick={onClickClose} role='presentation'>
              <span className='material-symbols-rounded'>close</span>
            </div>
          </div>
          <div className='inputForm-body'>
            <DaumPostcodeEmbed onComplete={handleComplete} />
          </div>
        </AddressModalRoot>
      </Modal>
    </ModalBackground>
  );
};
